/**
 * Scale filters that fixed-function sampling cannot provide. Each is a
 * fragment shader drawn on the item's quad; `default` and `bilinear` use the
 * regular sprite path instead.
 */
import type { ScaleFilter } from '../core/model';

// uRect is the cropped region in texels (x, y, w, h). Taps are clamped to it
// so pixels outside the crop never bleed in. uColor is the premultiplied item
// tint supplied by the mesh pipeline (carries opacity).
const HEADER = `#version 300 es
precision highp float;
in vec2 vUv;
out vec4 fragColor;
uniform sampler2D uTexture;
uniform vec2 uTexSize;
uniform vec4 uRect;
uniform vec4 uColor;
vec4 tap(vec2 p) {
  vec2 c = clamp(p, uRect.xy + 0.5, uRect.xy + uRect.zw - 0.5);
  return texture(uTexture, c / uTexSize);
}
vec2 position() { return uRect.xy + vUv * uRect.zw; }
`;

const POINT = `${HEADER}
void main() {
  fragColor = tap(floor(position()) + 0.5) * uColor;
}
`;

const BICUBIC = `${HEADER}
// Cubic convolution kernel with a = -0.5 (Keys 1981, "Cubic convolution interpolation"; Catmull-Rom).
float weight(float x) {
  x = abs(x);
  if (x <= 1.0) return (1.5 * x - 2.5) * x * x + 1.0;
  if (x < 2.0) return ((-0.5 * x + 2.5) * x - 4.0) * x + 2.0;
  return 0.0;
}
void main() {
  vec2 p = position() - 0.5;
  vec2 base = floor(p);
  vec2 f = p - base;
  vec4 sum = vec4(0.0);
  float total = 0.0;
  for (int j = -1; j <= 2; j++) {
    float wy = weight(float(j) - f.y);
    for (int i = -1; i <= 2; i++) {
      float w = wy * weight(float(i) - f.x);
      sum += tap(base + vec2(float(i), float(j)) + 0.5) * w;
      total += w;
    }
  }
  vec4 c = sum / total;
  c.a = clamp(c.a, 0.0, 1.0);
  c.rgb = clamp(c.rgb, 0.0, c.a);
  fragColor = c * uColor;
}
`;

const LANCZOS = `${HEADER}
const float PI = 3.141592653589793;
// Lanczos-3 windowed sinc: L(x) = sinc(x) * sinc(x / 3) for |x| < 3.
float weight(float x) {
  x = abs(x);
  if (x < 1e-4) return 1.0;
  if (x >= 3.0) return 0.0;
  float px = PI * x;
  return 3.0 * sin(px) * sin(px / 3.0) / (px * px);
}
void main() {
  vec2 p = position() - 0.5;
  vec2 base = floor(p);
  vec2 f = p - base;
  vec4 sum = vec4(0.0);
  float total = 0.0;
  for (int j = -2; j <= 3; j++) {
    float wy = weight(float(j) - f.y);
    for (int i = -2; i <= 3; i++) {
      float w = wy * weight(float(i) - f.x);
      sum += tap(base + vec2(float(i), float(j)) + 0.5) * w;
      total += w;
    }
  }
  vec4 c = sum / total;
  c.a = clamp(c.a, 0.0, 1.0);
  c.rgb = clamp(c.rgb, 0.0, c.a);
  fragColor = c * uColor;
}
`;

const AREA = `${HEADER}
// Box filter over the footprint of one output pixel in texel space. When
// magnifying, the footprint covers at most two texels per axis and the exact
// coverage weights reduce to a single bilinear fetch at an adjusted position.
void main() {
  vec2 p = position();
  vec2 fw = max(fwidth(p), vec2(1e-4));
  if (fw.x <= 1.0 && fw.y <= 1.0) {
    vec2 t = p - 0.5;
    vec2 base = floor(t);
    vec2 f = clamp((t - base - 0.5) / fw + 0.5, 0.0, 1.0);
    fragColor = tap(base + 0.5 + f) * uColor;
    return;
  }
  // Minifying: one bilinear fetch per covered texel, up to 8 per axis.
  vec2 n = clamp(ceil(fw), 1.0, 8.0);
  vec4 sum = vec4(0.0);
  for (int j = 0; j < 8; j++) {
    if (float(j) >= n.y) break;
    for (int i = 0; i < 8; i++) {
      if (float(i) >= n.x) break;
      vec2 o = (vec2(float(i), float(j)) + 0.5) / n - 0.5;
      sum += tap(p + o * fw);
    }
  }
  fragColor = sum / (n.x * n.y) * uColor;
}
`;

export type ResampleMode = 'point' | 'bicubic' | 'lanczos' | 'area';

export const RESAMPLE_FRAGMENTS: Record<ResampleMode, string> = {
  point: POINT,
  bicubic: BICUBIC,
  lanczos: LANCZOS,
  area: AREA,
};

/** null means the item is drawn by the regular bilinear sprite path. */
export function resampleMode(filter: ScaleFilter): ResampleMode | null {
  return filter === 'point' || filter === 'bicubic' || filter === 'lanczos' || filter === 'area' ? filter : null;
}
